import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getpoInwardAndInventoryById, updatePoInwardProduct } from '../services/po.stock.service';
import { HttpStatus } from 'pages/Core';
import notistack from 'pages/Core/Notistack';
import { useForm } from 'react-hook-form';
import { FormData } from '../type';
import { buildUpdatePoInwardPayload } from '../services/buildUpdatePoInwardPayload';
import { decryptItem } from 'helpers/secureNavigate';
import RejectProduct from './RejectProduct';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ViewRawMaterial from './ViewRawMaterial';

const EditPoInwardProduct = () => {
    const navigate = useNavigate();
    const { id, productName, materialType } = useParams();
    const decodedProductName = decodeURIComponent(productName || "");
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [productType, setProductType] = useState<string>(decryptItem(materialType as string) || '');
    const [isGold, setIsGold] = useState(productType.toLowerCase() === "gold")
    const [invoiceImage, setInvoiceImage] = useState<string | null>(null);
    const [invoicePreview, setInvoicePreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const rejectModalRef = useRef<any>(null);
    const [isViewOpen, setIsViewOpen] = useState(false)

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors }
    } = useForm<FormData>({
        mode: 'onChange'
    });

    const getProductDetails = async () => {

        try {
            setLoading(true);
            if (id) {
                const response = await getpoInwardAndInventoryById({ id: decryptItem(id as string) });
                if (response.status === HttpStatus.Success) {
                    const data = response.data;
                    Object.keys(data).forEach(key => {
                        setValue(key as keyof FormData, data[key]);
                    });

                    if (data.invoiceUrl) {
                        setInvoicePreview(data.invoiceUrl);
                        setInvoiceImage(data.invoiceUrl);
                    }
                }
            }
        } catch (error: any) {
            notistack.error(error.message || 'Failed to fetch product details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!id || !productName) {
            console.error('Missing required parameters');
            notistack.error('Invalid URL: Missing parameters');
            navigate('/po-inward');
            return;
        }
        getProductDetails();
    }, [id, productName]);

    const integerPattern = {
        value: /^\d+$/,
        message: 'Please enter a whole number (no decimals allowed)'
    };

    const weightPattern = {
        value: /^\d+(\.\d{1,3})?$/,
        message: 'Please enter up to 3 decimal places'
    };

    const ratePattern = {
        value: /^\d+(\.\d{1,2})?$/,
        message: 'Please enter up to 2 decimal places'
    };

    const textPattern = {
        value: /^[A-Za-z0-9\s\-_/@#$%&*()+=[\]{}|\\,.;:'"<>?!]*$/,
        message: 'Please enter valid characters'
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                setInvoiceImage(base64String);
                setInvoicePreview(base64String);
                setValue('invoiceUrl', base64String);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setInvoiceImage(null);
        setInvoicePreview(null);
        setValue('invoiceUrl', '');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = async (data: FormData) => {
        try {
            setSubmitting(true);
            const payload = buildUpdatePoInwardPayload(data);
            const response = await updatePoInwardProduct(payload);
            if (response.status === HttpStatus.Success) {
                notistack.notifyFromApi(response);
                navigate('/po-inward');
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error: any) {
            notistack.error(error.message || 'Failed to update product');
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, pattern?: RegExp) => {
        if (pattern) {
            const key = e.key;
            if (key === 'Backspace' || key === 'Delete' || key === 'Tab' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End') {
                return;
            }

            const currentValue = (e.target as HTMLInputElement).value;
            const newValue = currentValue + key;

            if (!pattern.test(newValue) && key !== '.') {
                e.preventDefault();
            }
        }
    };

    const handleCancel = () => {
        navigate('/po-inward');
    };

    const status = watch('status');

    useEffect(() => {
        if (status === 'Approved' || status === 'Rejected') {
            navigate('/po-inward');
        }
    }, [status]);

    if (loading) {
        return (
            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Body>
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        );
    }



    return (
        <>
            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="m-0">{decodedProductName} QC</h5>

                            <button className="add-btn"
                                onClick={() => {
                                    setIsViewOpen(true)
                                }}
                            >
                                View Raw Material
                            </button>
                        </Card.Header>
                        <Card.Body>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row">
                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Bag Number <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.bagNumber ? 'is-invalid' : ''}`}
                                            {...register('bagNumber', {
                                                required: 'Bag Number is required',
                                                pattern: textPattern
                                            })}
                                            disabled
                                            placeholder="Enter bag number"
                                        />
                                        {errors.bagNumber && (
                                            <small className="text-danger">{errors.bagNumber.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Expected Gold Rate <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.expectedGoldRate ? 'is-invalid' : ''}`}
                                            {...register('expectedGoldRate', {
                                                required: 'Expected Gold Rate is required',
                                                pattern: ratePattern
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            disabled
                                            placeholder="Enter expected gold rate"
                                        />
                                        {errors.expectedGoldRate && (
                                            <small className="text-danger">{errors.expectedGoldRate.message}</small>
                                        )}
                                    </div>

                                    {/* <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Expected Diamond Rate <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.expectedDiamondRate ? 'is-invalid' : ''}`}
                                            {...register('expectedDiamondRate', {
                                                required: 'Expected Diamond Rate is required',
                                                pattern: ratePattern
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            disabled
                                            placeholder="Enter expected diamond rate"
                                        />
                                        {errors.expectedDiamondRate && (
                                            <small className="text-danger">{errors.expectedDiamondRate.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Expected Making Rate <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.expectedMakingRate ? 'is-invalid' : ''}`}
                                            {...register('expectedMakingRate', {
                                                required: 'Expected Making Rate is required',
                                                pattern: ratePattern
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            disabled
                                            placeholder="Enter expected making rate"
                                        />
                                        {errors.expectedMakingRate && (
                                            <small className="text-danger">{errors.expectedMakingRate.message}</small>
                                        )}
                                    </div> */}

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Status <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.status ? 'is-invalid' : ''}`}
                                            {...register('status')}
                                            disabled
                                            placeholder="Enter status"
                                        />
                                        {errors.status && (
                                            <small className="text-danger">{errors.status.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Standard Gold Wt <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.standardGoldWeight ? 'is-invalid' : ''}`}
                                            {...register('standardGoldWeight', {
                                                required: 'Standard Gold Weight is required',
                                                pattern: weightPattern,
                                            })}
                                            disabled
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter standard gold weight"
                                        />
                                        {errors.standardGoldWeight && (
                                            <small className="text-danger">{errors.standardGoldWeight.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Standard Diamond Wt <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.standardDiamondWeight ? 'is-invalid' : ''}`}
                                            {...register('standardDiamondWeight', {
                                                required: 'Standard Diamond Weight is required',
                                                pattern: weightPattern,
                                            })}
                                            disabled
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter standard diamond weight"
                                        />
                                        {errors.standardDiamondWeight && (
                                            <small className="text-danger">{errors.standardDiamondWeight.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Quantity <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.qty ? 'is-invalid' : ''}`}
                                            {...register('qty', {
                                                required: 'Quantity is required',
                                                pattern: integerPattern,
                                                validate: (value) => parseInt(value as any) >= 1 || 'Quantity must be at least 1'
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*$/)}
                                            placeholder="Enter quantity"
                                        />
                                        {errors.qty && (
                                            <small className="text-danger">{errors.qty.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            IGI Number {!isGold && <span className="text-danger">*</span>}
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.igino ? 'is-invalid' : ''}`}
                                            {...register('igino', {
                                                required: !isGold ? 'IGI Number is required' : false,
                                                pattern: textPattern,
                                                maxLength: { value: 50, message: 'Maximum 50 characters allowed' }
                                            })}
                                            placeholder="Enter IGI number"
                                        />
                                        {errors.igino && (
                                            <small className="text-danger">{errors.igino.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            HUID {isGold && <span className="text-danger">*</span>}
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.huid ? 'is-invalid' : ''}`}
                                            {...register('huid', {
                                                required: isGold ? 'HUID is required' : false,
                                                pattern: textPattern,
                                                maxLength: { value: 50, message: 'Maximum 50 characters allowed' }
                                            })}
                                            placeholder="Enter HUID"
                                        />
                                        {errors.huid && (
                                            <small className="text-danger">{errors.huid.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            COCD Number <span className='text-danger'>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.cocdNo ? 'is-invalid' : ''}`}
                                            {...register('cocdNo', {
                                                required: 'COCD number is required',
                                                pattern: textPattern,
                                                maxLength: { value: 50, message: 'Maximum 50 characters allowed' }
                                            })}
                                            placeholder="Enter COCD number"
                                        />
                                        {errors.cocdNo && (
                                            <small className="text-danger">{errors.cocdNo.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Actual Gold Wt <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.actualGoldWeight ? 'is-invalid' : ''}`}
                                            {...register('actualGoldWeight', {
                                                required: 'Actual Gold Weight is required',
                                                pattern: weightPattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter actual gold weight"
                                        />
                                        {errors.actualGoldWeight && (
                                            <small className="text-danger">{errors.actualGoldWeight.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Actual Diamond Wt <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.actualDiamondWeight ? 'is-invalid' : ''}`}
                                            {...register('actualDiamondWeight', {
                                                required: 'Actual Diamond Weight is required',
                                                pattern: weightPattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter actual diamond weight"
                                        />
                                        {errors.actualDiamondWeight && (
                                            <small className="text-danger">{errors.actualDiamondWeight.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Gold Rate <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.goldCost ? 'is-invalid' : ''}`}
                                            {...register('goldCost', {
                                                required: 'Gold Rate is required',
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter gold rate"
                                        />
                                        {errors.goldCost && (
                                            <small className="text-danger">{errors.goldCost.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Diamond Rate <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.diamondCost ? 'is-invalid' : ''}`}
                                            {...register('diamondCost', {
                                                required: 'Diamond Rate is required',
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter diamond rate"
                                        />
                                        {errors.diamondCost && (
                                            <small className="text-danger">{errors.diamondCost.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Craftsmanship Rate
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.craftsmanshipCost ? 'is-invalid' : ''}`}
                                            {...register('craftsmanshipCost', {
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter craftsmanship rate"
                                        />
                                        {errors.craftsmanshipCost && (
                                            <small className="text-danger">{errors.craftsmanshipCost.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Making Charges <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.makingCharges ? 'is-invalid' : ''}`}
                                            {...register('makingCharges', {
                                                required: 'Making Charges is required',
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter making charges"
                                        />
                                        {errors.makingCharges && (
                                            <small className="text-danger">{errors.makingCharges.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Other Charges
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.otherCharges ? 'is-invalid' : ''}`}
                                            {...register('otherCharges', {
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter other charges"
                                        />
                                        {errors.otherCharges && (
                                            <small className="text-danger">{errors.otherCharges.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Total Rate <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.totalCost ? 'is-invalid' : ''}`}
                                            {...register('totalCost', {
                                                required: 'Total Rate is required',
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter total rate"
                                        />
                                        {errors.totalCost && (
                                            <small className="text-danger">{errors.totalCost.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            DP Price <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.dpPrice ? 'is-invalid' : ''}`}
                                            {...register('dpPrice', {
                                                required: 'DP Price is required',
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,2}$/)}
                                            placeholder="Enter DP price"
                                        />
                                        {errors.dpPrice && (
                                            <small className="text-danger">{errors.dpPrice.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Other Wt
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.otherWeight ? 'is-invalid' : ''}`}
                                            {...register('otherWeight', {
                                                pattern: weightPattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter other weight"
                                        />
                                        {errors.otherWeight && (
                                            <small className="text-danger">{errors.otherWeight.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Color Stone Rate
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.colorStonePrice ? 'is-invalid' : ''}`}
                                            {...register('colorStonePrice', {
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter color stone rate"
                                        />
                                        {errors.colorStonePrice && (
                                            <small className="text-danger">{errors.colorStonePrice.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Purchase Rate
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.purchaseRate ? 'is-invalid' : ''}`}
                                            {...register('purchaseRate', {
                                                pattern: ratePattern,
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter purchase rate"
                                        />
                                        {errors.purchaseRate && (
                                            <small className="text-danger">{errors.purchaseRate.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Cost To Company <span className='text-danger'>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.costToCompany ? 'is-invalid' : ''}`}
                                            {...register('costToCompany', {
                                                pattern: ratePattern,
                                                required: "Cost To Company is required",
                                                validate: (value) => !value || parseFloat(value as any) >= 0 || 'Value must be positive'
                                            })}
                                            onKeyDown={(e) => handleKeyDown(e, /^\d*\.?\d{0,3}$/)}
                                            placeholder="Enter cost to company"
                                        />
                                        {errors.costToCompany && (
                                            <small className="text-danger">{errors.costToCompany.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Barcode
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.barcode ? 'is-invalid' : ''}`}
                                            {...register('barcode', {
                                                pattern: textPattern,
                                                maxLength: { value: 50, message: 'Maximum 50 characters allowed' }
                                            })}
                                            placeholder="Enter barcode"
                                        />
                                        {errors.barcode && (
                                            <small className="text-danger">{errors.barcode.message}</small>
                                        )}
                                    </div>

                                    <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Invoice Image
                                        </label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="file"
                                                ref={fileInputRef}
                                                className="form-control form-control-sm"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                            />
                                            {invoicePreview && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm action-btn delete-btn"
                                                    onClick={handleRemoveImage}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="ml-2" />
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="hidden"
                                            {...register('invoiceUrl')}
                                        />
                                    </div>

                                    {/* <div className="col-md-3 mb-2">
                                        <label className="form-label">
                                            Is Received <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-control form-control-sm form-select form-select-sm ${errors.isRecieved ? 'is-invalid' : ''}`}
                                            {...register('isRecieved', {
                                                required: 'Is Received is required'
                                            })}
                                        >
                                            <option value="">Select</option>
                                            <option value="true">Yes</option>
                                            <option value="false">No</option>
                                        </select>
                                        {errors.isRecieved && (
                                            <small className="text-danger">{errors.isRecieved.message}</small>
                                        )}
                                    </div> */}

                                    <div className="col-md-6 mb-2">
                                        <label className="form-label">
                                            Special Instructions
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.specialInstructions ? 'is-invalid' : ''}`}
                                            {...register('specialInstructions', {
                                                maxLength: { value: 255, message: 'Maximum 255 characters allowed' }
                                            })}
                                            placeholder="Enter special instructions"
                                        />
                                        {errors.specialInstructions && (
                                            <small className="text-danger">{errors.specialInstructions.message}</small>
                                        )}
                                    </div>

                                    <input type="hidden" {...register('id', { valueAsNumber: true })} />
                                    <input type="hidden" {...register('purchaseOrderItemMasterId', { valueAsNumber: true })} />
                                    <input type="hidden" {...register('isSRP')} />
                                    <input type="hidden" {...register('deleted')} />
                                    <input
                                        type="hidden"
                                        {...register('updatedBy', { valueAsNumber: true })}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('createdBy', { valueAsNumber: true })}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('createdOn', { valueAsDate: true })}
                                    />
                                    <input
                                        type="hidden"
                                        {...register('updatedOn', { valueAsDate: true })}
                                    />
                                </div>

                                <div className="row mt-3">
                                    <div className="col-12 d-flex justify-content-end gap-2">
                                        <button
                                            type="button"
                                            className="btn add-btn"
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm add-btn reject-btn"
                                            title="Reject"
                                            onClick={() => {
                                                rejectModalRef.current?.open(decryptItem(id as string), decodedProductName);
                                            }}
                                        >
                                            Reject
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn submit-btn"
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Accepting...' : 'Accept'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
            <RejectProduct
                ref={rejectModalRef}
            />

            <ViewRawMaterial
                isViewOpen={isViewOpen}
                onClose={() => {
                    setIsViewOpen(false)
                }}
                id={decryptItem(id as string)}
            />
        </>
    );
};

export default EditPoInwardProduct;